import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { Editor } from '@tiptap/vue-2';
import { Markdown } from 'tiptap-markdown';
import CharacterCount from '@tiptap/extension-character-count';
import { onMounted, onUnmounted, ref, unref } from 'vue';
import {
  ResetFormattingOnEnter,
  SlashCommand,
  UserMention,
  HardBreakToList,
  handlePasteImageFileFromClipboard,
  ImageResize,
  VariableMention,
  useTiptapSlashCommand,
} from './extensions';
import SlashTriggerStyling from './extensions/SlashTriggerStyling';
import { unescapeHtmlEntities, sanitizeUrl } from './utils';
import { Extension } from '@tiptap/core';

/**
 * @typedef {object} ExtensionOptions
 * @property {boolean} [slashCommand=false] - Enable Slash Command extension
 * @property {boolean} [slashTriggerStyling=false] - Enable styling for slash command trigger
 * @property {boolean} [imageResize=false] - Enable image resize extension
 * @property {boolean} [variableMention=false] - Enable variable mention extension
 * @property {boolean} [hardBreak=true] - Enable Hard Break extension (Shift+Enter for line breaks)
 * @property {boolean} [link=true] - Enable Link extension
 * @property {boolean} [plainTextOnly=false] - Enable plain text only mode (disables rich formatting like bold, italic, code, heading)
 * @property {boolean} [enablePasteImageHandler=false] - Enable paste image handler in Tiptap (when false, parent component can handle paste events)
 */

/**
 * @typedef {object} TiptapOptions
 * @property {string} [value=''] - Initial editor content
 * @property {string} [placeholder=''] - Placeholder text when editor is empty
 * @property {('markdown'|'html'|'json')} [valueType='markdown'] - Format of the value
 * @property {boolean} [isEditable=true] - Whether the editor is editable
 * @property {ExtensionOptions} [extensions] - Configuration for optional extensions
 * @property {Extension[]} [customExtensions] - Configuration for custom extensions (not included in default extensions)
 * @property {function(string):void} [onUpdate] - Callback when content changes
 * @property {function():void} [onFocus] - Callback when editor is focused
 * @property {function():void} [onBlur] - Callback when editor loses focus
 * @property {function(string):void} [onUpdateHtml] - Callback with HTML content
 * @property {function(object): void} [onUpdateJson] - Callback with JSON content
 * @property {function(string):void} [onUpdateMarkdown] - Callback with Markdown content
 * @property {function(object):void} [onKeyDown] - Callback when a key is pressed
 * @property {function(object):void} [onSlashCommand] - Callback for slash commands
 * @property {function():void} [onCmdEnter] - Callback when Cmd/Ctrl + Enter is pressed
 * @property {function():void} [onCmdShiftEnter] - Callback when Cmd/Ctrl + Shift + Enter is pressed
 * @property {function(Editor):void} [onInstanceInitialized] - Callback when editor is initialized
 * @property {function(object):void} [onUploadImage] - Callback when an image is uploaded
 * @property {number} [characterLimit=0] - Character limit (0 means no limit)
 */

/** @type {TiptapOptions} */
const defaultOptions = {
  value: '',
  placeholder: '',
  valueType: 'markdown',
  isEditable: true,
  extensions: {
    slashCommand: false,
    slashTriggerStyling: false,
    imageResize: false,
    variableMention: false,
    hardBreak: true,
    link: true,
    plainTextOnly: false,
    enablePasteImageHandler: false,
  },
  customExtensions: [],
  onUpdate: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onUpdateHtml: () => {},
  onUpdateJson: () => {},
  onUpdateMarkdown: () => {},
  onKeyDown: () => {},
  onSlashCommand: () => {},
  onCmdEnter: () => {},
  onCmdShiftEnter: () => {},
  onInstanceInitialized: () => {},
  onUploadImage: () => {},
  characterLimit: 0,
};

export function useTiptapInstance(options = defaultOptions) {
  const editor = ref(null);
  const editorContent = ref('');
  const isFocused = ref(false);
  const isEditable = ref(options.isEditable);

  // Character count
  const characterCount = ref(0);
  const wordCount = ref(0);
  const characterLimit = ref(options.characterLimit || 0);

  /**
   * Updates character count manually when content is set programmatically
   */
  function updateCharacterCount() {
    // If character limit is 0, don't update character count
    if (options.characterLimit === 0) return;

    if (editor.value && editor.value.storage.characterCount) {
      characterCount.value = editor.value.storage.characterCount.characters();
      wordCount.value = editor.value.storage.characterCount.words();
    }
  }

  // Slash command composable
  const {
    isSlashCommandActive,
    currentCommandRange,
    toggleSlashCommandActive,
    replaceSlashCommand,
  } = useTiptapSlashCommand(editor);

  const toggle = {
    setValue: value => {
      // Guard against race condition where editor is destroyed or view is invalid
      // This can happen when component unmounts while async operations (like AI draft generation) complete
      if (
        !editor.value ||
        editor.value.isDestroyed ||
        !editor.value.view?.docView
      ) {
        return;
      }

      editor.value.commands.setContent(value);
      // Trigger character count update after setting content
      setTimeout(() => {
        updateCharacterCount();
      }, 0);
    },
    bold: () => {
      editor.value.chain().focus().toggleBold().run();
    },
    italic: () => {
      editor.value.chain().focus().toggleItalic().run();
    },
    bulletList: () => {
      editor.value.chain().focus().toggleBulletList().run();
    },
    orderedList: () => {
      editor.value.chain().focus().toggleOrderedList().run();
    },
    code: () => {
      editor.value.chain().focus().toggleCode().run();
    },
    link: () => {
      editor.value.chain().focus().toggleLink().run();
    },
    undo: () => {
      editor.value.chain().focus().undo().run();
    },
    redo: () => {
      editor.value.chain().focus().redo().run();
    },
    unsetLink: () => {
      editor.value.chain().focus().unsetLink().run();
    },
    setLink: url => {
      if (url) {
        // Sanitize URL to remove invalid characters (backslashes, trailing periods, etc.)
        url = sanitizeUrl(url);

        const hasProtocol =
          url.startsWith('http://') || url.startsWith('https://');

        if (!hasProtocol) {
          // Extract domain part if URL contains path (e.g., "example.com/path")
          const domainPart = url.split('/')[0];

          // Check if the domain part looks like a domain (contains at least one dot)
          const looksLikeDomain =
            /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+/i.test(
              domainPart
            );

          if (looksLikeDomain) {
            // Add https:// to the URL, preserving any path components
            url = `https://${url}`;
          }
        }
      }

      editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();

      // editor.value.chain().focus().setLink({ href: url }).run();
    },
    focus: () => {
      // Guard against race condition where editor is destroyed or view is invalid
      // This can happen when component unmounts while async operations complete
      if (
        !editor.value ||
        editor.value.isDestroyed ||
        !editor.value.view?.docView
      ) {
        return;
      }
      editor.value.chain().focus().run();
    },
    setReadonly: (value = true) => {
      if (editor.value) {
        isEditable.value = !value;
        editor.value.setEditable(!value);
      }
    },
    setEditable: (value = true) => {
      if (editor.value) {
        isEditable.value = value;
        editor.value.setEditable(value);
      }
    },
    insertText: value => {
      // Get the current selection
      const { from, to } = editor.value.state.selection;

      const actionString = `${value}`;

      // Insert the text directly
      editor.value.view.dispatch(
        editor.value.view.state.tr.insertText(actionString, from, to)
      );

      // Focus the editor
      toggle.focus();
    },
    clear: () => {
      if (!editor.value || editor.value.isDestroyed) {
        return;
      }
      editor.value.commands.clearContent();
    },
    blur: () => {
      if (
        !editor.value ||
        editor.value.isDestroyed ||
        !editor.value.view?.docView
      ) {
        return;
      }
      editor.value.chain().focus().blur().run();
    },
    insertImage: attributes => {
      if (!editor.value || editor.value.isDestroyed) {
        return;
      }
      editor.value.commands.insertImageResize(attributes);
    },
    updateImageSize: (width, height = null) => {
      if (!editor.value || editor.value.isDestroyed) {
        return;
      }
      editor.value.commands.updateImageSize(width, height);
    },
  };

  /**
   * Checks if a specific format is active in the editor
   * @param {string} type - The format type to check (e.g., 'bold', 'italic', 'link')
   * @param {object} attrs - Optional attributes to check
   */
  function isActive(type, attrs = {}) {
    return editor.value?.isActive(type, attrs) ?? false;
  }

  /**
   * Checks if undo operation is available
   * @returns {boolean} - Whether undo can be performed
   */
  function canUndo() {
    return editor.value ? editor.value.can().undo() : false;
  }

  /**
   * Checks if redo operation is available
   * @returns {boolean} - Whether redo can be performed
   */
  function canRedo() {
    return editor.value ? editor.value.can().redo() : false;
  }

  /**
   * Checks if a link can be added to the editor
   * @returns {boolean} - Whether a link can be added
   */
  function canAddLink() {
    if (!editor.value) return false;
    const { from, to } = editor.value.state.selection;
    // Check if there's text selected or if a link is already active
    return from !== to || isActive('link');
  }

  // Event handlers
  function onBlur() {
    isFocused.value = false;

    if (options.onBlur) options.onBlur();
  }
  function onFocus() {
    isFocused.value = true;

    if (options.onFocus) options.onFocus();
  }
  function onUpdate() {
    const markdownContent = unescapeHtmlEntities(
      editor.value.storage.markdown.getMarkdown()
    );
    const htmlContent = editor.value.getHTML();
    const jsonContent = editor.value.getJSON();

    // Update character count if extension is available
    if (editor.value.storage.characterCount) {
      characterCount.value = editor.value.storage.characterCount.characters();
      wordCount.value = editor.value.storage.characterCount.words();
    }

    const getValueByType = () => {
      if (options.valueType === 'markdown') return markdownContent;
      if (options.valueType === 'html') return htmlContent;
      return jsonContent; // default to JSON if not markdown or HTML
    };

    const defaultValue = getValueByType();
    editorContent.value = defaultValue;

    // Default value
    if (options.onUpdate) options.onUpdate(defaultValue);

    // HTML
    if (options.onUpdateHtml) options.onUpdateHtml(htmlContent);

    // JSON
    if (options.onUpdateJson) options.onUpdateJson(jsonContent);

    // Markdown
    if (options.onUpdateMarkdown) options.onUpdateMarkdown(markdownContent);
  }
  function onPasteImageFromClipboard(clipboardData) {
    const html = clipboardData.getData('text/html');

    if (html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const img = tempDiv.querySelector('img');

      // If there is an image tag with an http/https src, let Tiptap handle it.
      if (img && img.src && img.src.startsWith('http')) {
        return false; // This allows Tiptap's default paste handler to run
      }
    }

    const isFiles = clipboardData.types.includes('Files');

    if (isFiles) {
      const imageFile = handlePasteImageFileFromClipboard(
        editor,
        clipboardData.items
      );
      if (imageFile) {
        return true; // File paste handled (e.g., an image was inserted)
      }
    }

    return false;
  }

  /**
   * Sets the content of the editor
   * @param {string} value - The content to set
   */
  function setEditorContent(value) {
    toggle.setValue(value);
    editorContent.value = value;

    // Manually update character count since programmatic content changes
    // might not trigger the onUpdate handler consistently
    setTimeout(() => {
      updateCharacterCount();
    }, 0);
  }

  /**
   * Inline composable for handling editor link functionality
   */
  const useEditorLink = () => {
    const current = ref('');

    const reset = () => {
      current.value = '';
    };

    const load = () => {
      reset();
      if (editor.value && isActive('link')) {
        // Get the current link URL from the editor
        current.value = editor.value.getAttributes('link').href || '';
        // Initialize the new link with the current link value
      }
    };

    const unset = () => {
      toggle.unsetLink();
    };

    const set = url => {
      toggle.setLink(url);
    };

    return { current, load, reset, unset, set };
  };

  // Optional extensions (included in extensions)
  function initializeOptionalExtensions() {
    const optionalExtensions = [];

    // Hard break
    if (
      options.extensions?.hardBreak !== false &&
      (options.extensions?.hardBreak ?? defaultOptions.extensions.hardBreak)
    ) {
      const hardBreakExtension = HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => editor.value.commands.setHardBreak(),
          };
        },
      });
      optionalExtensions.push(hardBreakExtension);
      optionalExtensions.push(HardBreakToList);
    } else {
      const ShiftEnterAsNewLine = Extension.create({
        name: 'resetFormattingOnEnter',
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': ({ editor: editorInstance }) => {
              editorInstance.commands.splitBlock();

              return true;
            },
          };
        },
      });

      optionalExtensions.push(ShiftEnterAsNewLine);
    }

    // Slash command
    if (
      options.extensions?.slashCommand ||
      defaultOptions.extensions.slashCommand
    ) {
      const slashCommandExtension = SlashCommand({
        HTMLAttributes: {
          class: 'slash-command-trigger',
        },
        onSlashCommand: params => {
          if (params.type === 'open') {
            isSlashCommandActive.value = true;
            currentCommandRange.value = params.range;
          } else if (params.type === 'close') {
            isSlashCommandActive.value = false;
            currentCommandRange.value = null;
          } else if (params.type === 'search') {
            currentCommandRange.value = params.range;
          }

          // Call the user's onSlashCommand handler
          options.onSlashCommand?.(params);
        },
      });
      optionalExtensions.push(slashCommandExtension);
    }

    // Slash trigger styling
    if (
      options.extensions?.slashTriggerStyling ||
      defaultOptions.extensions.slashTriggerStyling
    ) {
      optionalExtensions.push(SlashTriggerStyling);
    }

    // Image resize
    if (
      options.extensions?.imageResize ||
      defaultOptions.extensions.imageResize
    ) {
      optionalExtensions.push(
        ImageResize.configure({
          onUploadImage: options.onUploadImage,
        })
      );
    }

    // Variable mention
    if (
      options.extensions?.variableMention ||
      defaultOptions.extensions.variableMention
    ) {
      optionalExtensions.push(VariableMention);
    }

    return optionalExtensions;
  }

  // Custom extensions (not included in extensions, come from the user)
  function initializeCustomExtensions() {
    let customExtensions = [];
    if (options.customExtensions && options.customExtensions.length > 0) {
      customExtensions = options.customExtensions;
    }

    return customExtensions;
  }

  onMounted(() => {
    const defaultExtensions = [
      Markdown.configure({
        breaks: true, // New lines in markdown input are converted to <br>
      }),
      Document,
      Paragraph,
      Text,
      History,
      ResetFormattingOnEnter,
      UserMention,
    ];

    // Rich formatting extensions (disabled in plain text mode)
    const richFormattingExtensions = [];
    if (
      !options.extensions?.plainTextOnly &&
      !(
        options.extensions?.plainTextOnly ??
        defaultOptions.extensions.plainTextOnly
      )
    ) {
      richFormattingExtensions.push(Heading, Bold, Italic, Code);
    }

    // List extensions (always enabled, even in plain text mode)
    const listExtensions = [BulletList, OrderedList, ListItem];
    const defaultCustomExtensions = [
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({
        placeholder: unref(options.placeholder),
        showOnlyWhenEditable: false,
      }),
    ];

    // Conditionally add Image extension only if ImageResize is NOT enabled
    if (
      !options.extensions?.imageResize &&
      !(
        options.extensions?.imageResize ?? defaultOptions.extensions.imageResize
      )
    ) {
      defaultCustomExtensions.push(
        Image.extend({
          parseHTML() {
            return [
              {
                tag: 'img[src]:not([src^="data:"])', // Ignore data URLs. equivalent to allowBase64=false
                getAttrs: dom => {
                  const src = dom.getAttribute('src');
                  if (!src) return null;

                  try {
                    const url = new URL(src);
                    const height = url.searchParams.get('cw_image_height');

                    return {
                      src,
                      height: height || null,
                    };
                  } catch {
                    return { src };
                  }
                },
              },
            ];
          },
          addAttributes() {
            return {
              ...Image.config.addAttributes(),
              height: {
                default: null,
                renderHTML: attributes => {
                  if (!attributes.height) return {};

                  return {
                    style: `height: ${attributes.height}`,
                  };
                },
              },
            };
          },
        }).configure({
          HTMLAttributes: {
            class: 'tiptap-image',
          },
        })
      );
    }

    // Add Link extension conditionally
    if (
      options.extensions?.link !== false &&
      (options.extensions?.link ?? defaultOptions.extensions.link)
    ) {
      defaultCustomExtensions.push(
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class:
              'text-brand-link hover:text-brand-link-hover hover:underline',
          },
        })
      );
    }

    // Add CharacterCount extension if character limit is set
    if (options.characterLimit && options.characterLimit > 0) {
      defaultCustomExtensions.push(
        CharacterCount.configure({
          limit: options.characterLimit,
        })
      );
    }

    // Create the editor instance
    editor.value = new Editor({
      content: options.value,
      extensions: [
        ...defaultExtensions,
        ...richFormattingExtensions,
        ...listExtensions,
        ...defaultCustomExtensions,
        ...initializeOptionalExtensions(),
        ...initializeCustomExtensions(),
      ],
      onFocus,
      onBlur,
      onUpdate,
      editable: isEditable.value,
      editorProps: {
        handleKeyDown: (_view, event) => {
          options.onKeyDown?.(event);

          // Handle Cmd/Ctrl + Shift + Enter
          if (
            (event.metaKey || event.ctrlKey) &&
            event.shiftKey &&
            event.key === 'Enter'
          ) {
            options.onCmdShiftEnter?.(event);
            // Prevent default Enter behavior
            return true;
          }

          // Handle Cmd/Ctrl + Enter.
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            options.onCmdEnter?.(event);
            // Prevent default Enter behavior (new line)
            return true;
          }

          return false;
        },
        handlePaste: (_view, event) => {
          // If enablePasteImageHandler is not enabled, skip this handler
          // This allows parent components to handle paste events instead
          if (
            !options.extensions?.enablePasteImageHandler &&
            !(
              options.extensions?.enablePasteImageHandler ??
              defaultOptions.extensions.enablePasteImageHandler
            )
          ) {
            return false;
          }

          // If no onUploadImage callback is provided, don't handle paste events
          if (!options.onUploadImage) return false;

          const clipboardData =
            event.clipboardData || event.originalEvent.clipboardData;

          const imageFromClipboard = onPasteImageFromClipboard(clipboardData);
          if (imageFromClipboard) return true;

          return false;
        },
      },
    });

    editorContent.value = options.value;

    // Update character count after editor is initialized
    setTimeout(() => {
      updateCharacterCount();
    }, 0);

    if (options.onInstanceInitialized)
      options.onInstanceInitialized(editor.value);
  });

  onUnmounted(() => {
    editor.value.destroy();
  });

  return {
    editor,
    editorContent,
    toggle,
    isFocused,
    isEditable,
    isActive,
    canAddLink,
    unescapeHtmlEntities,
    canUndo,
    canRedo,
    setEditorContent,
    link: useEditorLink(),
    replaceSlashCommand,
    isSlashCommandActive,
    toggleSlashCommandActive,
    characterCount,
    wordCount,
    characterLimit,
    updateCharacterCount,
  };
}
